import { AxiosInstance } from 'axios'
import { SolarPagedResponse } from 'api-client/foundation'
import { HospitalReader } from 'api-client/src/hospital'

import { buildApiEndpoint, createSolarApiClient } from '@/shared/libs/api-client'
import {
  HospitalView,
  SearchHospital,
  SearchHospitalsParams,
  HospitalInList,
  GetHospitalsParams,
} from '@/features/hospital/types/hospital.type'

// MIGRATED FROM: api-client/src/hospital/UserApiHospital.ts
// MIGRATION DATE: 2024-08-24
// CHANGE TYPE: ENHANCED - Integrated api-client with Solar API patterns
// BACKWARD COMPATIBLE: Yes - All existing cheetah methods preserved

/**
 * Hospital Service
 *
 * Provides hospital-related API operations with Solar API patterns.
 * Integrates with api-client HospitalReader using composition pattern.
 *
 * Available methods from API-client:
 * - hospitalReader.getHospitalShareLink(hospitalId): Get hospital share link
 * - hospitalReader.getHospitalLogDetail(hospitalId): Get hospital log details
 * - hospitalReader.getVisitorOrder(hospitalId): Get visitor order information
 * - hospitalReader.getVisitOrderSummaries(hospitalId): Get visit order summaries
 * - hospitalReader.postVisitorOrderTracking(hospitalId, token?): Track visitor orders
 * - hospitalReader.getTreatmentReviewStatistics(hospitalId): Get treatment review statistics
 * - hospitalReader.getSatisfactionStatistics(hospitalId): Get satisfaction statistics
 */
export class HospitalService {
  // API-client integration via composition
  private readonly hospitalReader: HospitalReader

  constructor(private readonly apiClient: AxiosInstance) {
    // Initialize API-client instance
    this.hospitalReader = new HospitalReader(apiClient)
  }

  // EXISTING: Preserved from cheetah implementation with Solar API pattern
  async getHospital(hospitalId: number): Promise<HospitalView> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/hospital/user/queries/get-hospital/process',
        data: {
          hospitalId,
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<HospitalView>(config)

    return response.data
  }

  // EXISTING: Search hospitals with Solar API pattern
  async searchHospitals(params: SearchHospitalsParams): Promise<SolarPagedResponse<SearchHospital[]>> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: '/search/hospitals',
        params,
      },
      'solar',
    )

    const response = await this.apiClient.request<SolarPagedResponse<SearchHospital[]>>(config)

    return response.data
  }

  // EXISTING: Get hospitals list with Solar API pattern
  async getHospitals(params: GetHospitalsParams): Promise<SolarPagedResponse<HospitalInList[]>> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: '/hospital/infos',
        params,
      },
      'solar',
    )

    const response = await this.apiClient.request<SolarPagedResponse<HospitalInList[]>>(config)

    return response.data
  }

  // ========================
  // API-CLIENT INTEGRATION - Enhanced methods using api-client
  // ========================

  /**
   * @method HospitalReader.getHospitalShareLink
   * @path GET /hospital/share-link
   * @returns Promise<SolarResponse<{ shareLink: string }>>
   */
  getHospitalShareLink = (hospitalId: number) => this.hospitalReader.getHospitalShareLink(hospitalId)

  /**
   * @method HospitalReader.getHospitalLogDetail
   * @path GET /hospital/log-detail
   * @returns Promise<HospitalLogDetail>
   */
  getHospitalLogDetail = (hospitalId: number) => this.hospitalReader.getHospitalLogDetail(hospitalId)

  /**
   * @method HospitalReader.getVisitorOrder
   * @path GET /hospital/visitor-order
   * @returns Promise<AxiosResponse<HospitalVisitorOrderView>>
   */
  getVisitorOrder = (hospitalId: number) => this.hospitalReader.getVisitorOrder(hospitalId)

  /**
   * @method HospitalReader.getVisitOrderSummaries
   * @path GET /hospital/visit-order-summaries
   * @returns Promise<AxiosResponse<HospitalVisitorOrderSummariesView>>
   */
  getVisitOrderSummaries = (hospitalId: number) => this.hospitalReader.getVisitOrderSummaries(hospitalId)

  /**
   * @method HospitalReader.postVisitorOrderTracking
   * @path POST /hospital/visitor-order-tracking
   * @returns Promise<void>
   */
  postVisitorOrderTracking = (hospitalId: number, liveActivityDeviceToken?: string) =>
    this.hospitalReader.postVisitorOrderTracking(hospitalId, liveActivityDeviceToken)

  /**
   * @method HospitalReader.getTreatmentReviewStatistics
   * @path GET /hospital/treatment-review-statistics
   * @returns Promise<TreatmentReviewStatistics>
   */
  getTreatmentReviewStatistics = (hospitalId: number) => this.hospitalReader.getTreatmentReviewStatistics(hospitalId)

  /**
   * @method HospitalReader.getSatisfactionStatistics
   * @path GET /hospital/satisfaction-statistics
   * @returns Promise<SatisfactionStatistics>
   */
  getSatisfactionStatistics = (hospitalId: number) => this.hospitalReader.getSatisfactionStatistics(hospitalId)
}

export const clientHospitalService = new HospitalService(createSolarApiClient())
